import React, { useState, useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import {
  Box,
  Button,
  createToast,
  CheckboxInput,
  CheckboxLabel,
  Input,
  Modal,
  RadioInput,
  RadioLabel,
  Text
} from '@maui/elements'
import { useReportsVocabApi } from '@nautilusjs/mati-apis'
import { registerModal } from '../AppModal'
import useUpgradeSubscription from '../../store/upgrade/hooks'
import { STATE_REQUESTING, STATE_SUCCESS } from '../../store/upgrade/constants'
import styled from 'styled-components'
import { closeModal } from '../../store/appModal/actions'
import subscriptionTypes from '../../store/upgrade/subscriptionTypes'
import { DropDownSelect } from '../DropDownSelect'
import { translate } from '../../utils'

const ModalSection = styled(Box)`
  width: 50%;
`

const ButtonGrouping = styled(Box)`
  display: flex;
  justify-content: flex-end;

  button {
    margin-left: 10px;
  }
`

const ErrorMessage = ({ error }) => {
  if (!error) { return null }

  return (
    <Box mt="10px" color="action-destructive">
      {error}
    </Box>
  )
}

const UpgradeCheckboxRowContainer = styled(Box)`
  display: flex;
`

function UpgradeModal({ sku, ...modalProps }) {
  const dispatch = useDispatch()
  const { state, error, upgradeSubscription } = useUpgradeSubscription()
  const { data: reportsVocab } = useReportsVocabApi()
  const locations = reportsVocab?.locations

  const [errorState, setErrorState] = useState({})
  const [formState, setFormState] = useState({
    sku: '',
    company_country: '',
    has_active_project: '',
    interest_reason: '',
    phone_no: '',
    preferred_contact_method: 'Email'
  })

  const updateFormField = useCallback((name, value) => {
    // Merge in the new value
    setFormState({ ...formState, [name]: value })

    // If there are no errors or there are no errors for this field, return
    if (!errorState.errors?.[name]) { return }

    // Otherwise destructure out the errors for this field
    const { [name]: toRemove, ...newErrors } = errorState.errors
    setErrorState({ ...errorState, errors: newErrors })
  }, [formState, errorState, setFormState, setErrorState])

  // wrapper around update to process sku comma delimited format
  const updateSku = useCallback((sku, included) => {
    const skus = formState.sku.split(',').filter(Boolean)
    const skuSet = new Set(skus)

    if (included && sku) {
      skuSet.add(sku)
    } else {
      skuSet.delete(sku)
    }

    updateFormField('sku', Array.from(skuSet).join(','))
  }, [formState, updateFormField])

  const submitForm = useCallback(() => {
    upgradeSubscription(formState)
  }, [formState, upgradeSubscription])

  const SkuCheckbox = useCallback(({ sku, label }) => (
    <CheckboxLabel margin='5px' width='300px'>
      <CheckboxInput
        checked={formState.sku.includes(sku)}
        onChange={e => {
          updateSku(sku, e.target.checked)
        }}
      />
      {label}
    </CheckboxLabel>
  ), [formState, updateSku])

  // On service error, update form error state & messaging
  useEffect(() => {
    if (error && error.status === 422) {
      createToast({
        variant: 'error',
        title: 'Error',
        message: 'Please correct the form before clicking submit',
        duration: 5000,
      })
      setErrorState({
        message: 'Please correct the form above before clicking submit',
        errors: error.body.errors
      })
    } else if (error) {
      createToast({
        variant: 'error',
        title: 'Error',
        message: translate('dataMessages.upgradeError'),
        duration: 5000,
      })
      setErrorState({
        message: error.ui ? error.message : 'An unexpected error has occurred'
      })
    } else {
      setErrorState({})
    }
  }, [error, setErrorState])

  // On state change to success, close the modal and scroll to screen top
  useEffect(() => {
    if (state === STATE_SUCCESS) {
      createToast({
        variant: 'success',
        title: 'Request sent',
        message: translate('dataMessages.upgradeSuccess'),
        duration: 9000,
      })
      dispatch(closeModal('UpgradeModal'))
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [dispatch, state])

  const requesting = state === STATE_REQUESTING
  const { errors = {} } = errorState

  return (
    <Modal {...modalProps} size="large">
      <Modal.Header>
        <Modal.Title>Subscription Upgrade</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          <p>
            Thank you for your interest in Mandiant Advantage: Threat Intelligence
            subscriptions. Due to the sensitive nature of Mandiant Threat Intelligence,
            your upgrade request will be handled personally by one of our experts after
            completing the following questions.
          </p>
          <ModalSection>
            <div>
              <p>
                Where is your company headquarters?
              </p>
              <DropDownSelect
                error={errors.company_country}
                selectedItem={formState.company_country}
                onClick={value => updateFormField('company_country', value)}
                list={locations}
                placeholder={'Select Country'}
                menuWidth='350px'
              />
              <ErrorMessage error={errors.company_country && 'This is a required field'} />
            </div>

            <div>
              <p>Do you have an active Threat Intelligence Project?</p>
              <DropDownSelect
                error={errors.has_active_project}
                selectedItem={formState.has_active_project}
                onClick={value => updateFormField('has_active_project', value)}
                list={['Yes', 'No']}
                placeholder={'Yes or No'}
                menuWidth='350px'
              />
              <ErrorMessage error={errors.has_active_project && 'This is a required field'} />
            </div>

            <div>
              <p>Why are you interested in Mandiant Advantage?</p>
              <DropDownSelect
                error={errors.interest_reason}
                selectedItem={formState.interest_reason}
                onClick={value => updateFormField('interest_reason', value)}
                list={['Internal Project', 'Partnership', 'Academic', 'Other']}
                placeholder={'Select Reason'}
                menuWidth='350px'
              />
              <ErrorMessage error={errors.interest_reason && 'This is a required field'} />
            </div>

            <div>
              <p>What upgrade packages are you interested in?</p>
              <UpgradeCheckboxRowContainer>
                <SkuCheckbox sku={subscriptionTypes.SEC_OPS} label="Security Operations" />
                <SkuCheckbox sku={subscriptionTypes.FUSION} label="Fusion" />
              </UpgradeCheckboxRowContainer>

              <UpgradeCheckboxRowContainer>
                <SkuCheckbox sku={subscriptionTypes.DTM} label="Dark Web Search" />
                <SkuCheckbox sku={subscriptionTypes.VULN} label="Vulnerabilities" />
              </UpgradeCheckboxRowContainer>

              <UpgradeCheckboxRowContainer>
                <SkuCheckbox sku={subscriptionTypes.EIB} label="Executive Intelligence Briefings" />
              </UpgradeCheckboxRowContainer>
              <ErrorMessage error={errors.sku && 'Please select at least one upgrade package'} />
            </div>

            <div>
              <p>What is your preferred contact method?</p>
              <Box display='flex' alignItems='center' my='spaceGapMedium'>
                <RadioLabel mb='0' padding='0'>
                  <RadioInput
                    value='Email'
                    name='Email'
                    checked={formState.preferred_contact_method === 'Email'}
                    onChange={e => {
                      if (e.target.checked) {
                        updateFormField('preferred_contact_method', 'Email')
                      }
                    }}
                  />
                </RadioLabel>
                <Text ml='spaceGapMedium' variant='heading6'>Email</Text>
              </Box>
              <Box display='flex' alignItems='center' mt='spaceGapMedium' mb='spaceGapXLarge'>
                <RadioLabel mb='0' padding='0'>
                  <RadioInput
                    value='Phone'
                    name='Phone'
                    checked={formState.preferred_contact_method === 'Phone'}
                    onChange={e => {
                      if (e.target.checked) {
                        updateFormField('preferred_contact_method', 'Phone')
                      }
                    }}
                  />
                </RadioLabel>
                <Text ml='spaceGapMedium' variant='heading6'>Phone</Text>
              </Box>
              <Input
                name='Phone'
                height='32px'
                placeholder='Phone'
                width="100%"
                defaultValue={formState.phone_no}
                onChange={e => {
                  updateFormField('phone_no', e.target.value)
                }}
                borderColor={errors.phone_no?.[0] && 'colorFeedbackError'}
              />
              <ErrorMessage error={errors.phone_no?.[0]} />
            </div>
          </ModalSection>

          <p>
            Once you hit submit a team member will be in contact based on your contact preferences above.
          </p>

          <ErrorMessage error={errorState.message} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGrouping>
          <Button
            as="button"
            variant="secondary"
            onClick={() => dispatch(closeModal('UpgradeModal'))}
          >
            Cancel
          </Button>
          <Button
            as="button"
            variant="primary"
            disabled={requesting}
            onClick={submitForm}
          >
            Submit
          </Button>
        </ButtonGrouping>
      </Modal.Footer>
    </Modal>
  )
}

registerModal('UpgradeModal', UpgradeModal)

export default UpgradeModal
